import React, { FunctionComponent, SyntheticEvent } from 'react';
import classnames from 'classnames';
import withStyles from 'react-jss';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { ClassProp } from 'helpers/types';
import { Theme } from 'config';

import FormErrors from '../FormError';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  classes: Styles;
  type: string;
  name: string;
  placeholder: string;
  label: string;
  register: any;
  appendLabel: string;
  size?: string;
  tabIndex?: number;
  step?: string;
};

const styles = (theme: Theme) => ({
  container: {
    width: '100%',
    marginTop: '.25rem !important',
    justifyContent: 'center !important',
  },
  small: {
    width: '31%',
    marginRight: '.55rem',
  },
  medium: {
    width: '48%',
    marginRight: '.55rem',
  },
  large: {
    width: '100%',
  },
  fieldContainer: {},
  fieldArray: {
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
  fieldArrayItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '.75rem',
  },
  fieldLabel: {
    color: '#646464 !important',
    fontSize: '.875rem !important',
    fontWeight: '500 !important',
    margin: '0 0 .30rem 0',
    display: 'block !important',
  },
  fieldInput: {
    display: 'flex',
    maxWidth: '76%',
    minWidth: '76%',
    textAlign: 'left',
    margin: '0 .25rem 0 0',
    outline: '0',
    tapHighlightColor: 'rgba(255,255,255,0)',
    lineHeight: '1.21em',
    padding: '.67857143rem 1rem',
    fontSize: '1em',
    background: '#fff',
    border: '.0625rem solid rgba(34,36,38,.15)',
    color: 'rgba(0,0,0,.87)',
    borderRadius: '.28571429rem',
    boxShadow: '0 0 0 0 transparent inset',
    transition: 'color .1s ease,border-color .1s ease',
  },
  button: {
    borderRadius: '1.5em !important',
    color: '#fff !important',
    fontWeight: 'bold !important',
    padding: '.5rem 1rem',
    outline: 0,
    border: 'none !important',
    '&:hover': {
      boxShadow:
        '0 0 0 .0625rem transparent inset,0 0 0 0 rgba(34,36,38,.15) inset !important',
      opacity: 0.9,
    },
    '&:active': {
      opacity: 0.7,
    },
  },
  fieldArrayRemoveBtn: {
    backgroundColor: `${theme.btnGray} !important`,
  },
  fieldArrayAddBtn: {
    backgroundColor: `${theme.violet} !important`,
    marginTop: '.5rem !important',
  },
  fieldError: {},
  '@global input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
    '-webkitAppearance': 'none',
    margin: '0',
  },
  '@global input[type=number]': {
    '-mozAppearance': 'textfield',
  },
});

const DynamicFormInput: FunctionComponent<Props> = ({
  classes,
  label,
  appendLabel,
  name,
  size,
  register,
  ...rest
}) => {
  const { control, formState } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    name,
    control,
  });

  const { errors } = formState;

  const handleAppend = () => {
    append({ name: '' });
  };

  const handleRemove = (e: SyntheticEvent<HTMLButtonElement, Event>) => {
    const value = (e.target as HTMLButtonElement).value;
    remove(parseInt(value));
  };
  return (
    <div
      className={classnames(classes.container, {
        [classes.small]: size === 'small',
        [classes.medium]: size === 'medium',
        [classes.large]: size === 'large',
      })}
    >
      <label htmlFor={name}></label>
      <span className={classes.fieldLabel}>{label}</span>
      <ul className={classes.fieldArray}>
        {fields.map((item, index) => (
          <li key={item.id} className={classes.fieldArrayItem}>
            <input
              className={classes.fieldInput}
              id={name}
              name={`${name}[${index}].name`}
              {...rest}
              ref={register}
            />
            {index !== 0 && (
              <button
                className={classnames(
                  classes.button,
                  classes.fieldArrayRemoveBtn,
                )}
                type="button"
                value={index}
                onClick={handleRemove}
              >
                Remove
              </button>
            )}
          </li>
        ))}
      </ul>
      <button
        className={classnames(classes.button, classes.fieldArrayAddBtn)}
        type="button"
        onClick={handleAppend}
      >
        {appendLabel}
      </button>
      {errors && <FormErrors name={name} errors={errors} />}
    </div>
  );
};
export default withStyles(styles)(DynamicFormInput);
